import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider
} from '@mui/material';
import { useTheme } from "@emotion/react";
import { tokens } from "../theme";
import PropTypes from 'prop-types';
import { PrimaryButton, SecondaryButton, IconContainer, DialogTitleText, SubTitleScreen } from "./Styles";

const MuiFullScreenDialog = ({
    open,
    onClose,
    icon,
    title,
    subtitle,
    content,
    actionTitle,
    onAction,
    maxWidth = 'lg',
    allowBackdropClose = false,
    allowEscapeClose = false,
    showActions = true,
    showCloseButton = true,
    disableTransitions = true,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const handleDialogClose = (event, reason) => {
        // Ignore backdrop/Escape close unless explicitly allowed
        if (!allowBackdropClose && reason === 'backdropClick') return;
        if (!allowEscapeClose && reason === 'escapeKeyDown') return;
        onClose?.(event, reason);
    };

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={handleDialogClose}
            sx={{
                '& .MuiDialog-paper': {
                    bgcolor: 'background.default'
                }
            }}
            maxWidth={maxWidth}
            keepMounted
            disableEscapeKeyDown={!allowEscapeClose}
            // Eliminate visual flicker and focus jumps
            transitionDuration={disableTransitions ? 0 : undefined}
            TransitionProps={disableTransitions ? { appear: false, timeout: 0 } : undefined}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
        >
            <DialogTitle
                sx={{
                    m: 0,
                    p: 1,
                    bgcolor: colors.grey[70],
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {icon && (
                        <div style={IconContainer(colors)}>
                            {React.cloneElement(icon, { sx: { fontSize: "30px" } })}
                        </div>
                    )}
                    <Box>
                        <Typography sx={DialogTitleText(colors)}>
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography sx={{ ...SubTitleScreen(colors), fontSize: 15 }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 1 }}>
                {content}
            </DialogContent>

            {showActions && (
                <>
                    <Divider />
                    <DialogActions sx={{ p: 1, bgcolor: colors.grey[70] }}>
                        {showCloseButton && (
                            <Button 
                                onClick={(e) => onClose?.(e, 'closeButton')}
                                sx={PrimaryButton(colors)}
                            >
                                Close
                            </Button>
                        )}
                        {onAction && (
                            <Button
                                onClick={onAction}
                                sx={PrimaryButton(colors)}
                            >
                                {actionTitle || 'Save'}
                            </Button>
                        )}
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

MuiFullScreenDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    icon: PropTypes.element,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    content: PropTypes.node.isRequired,
    actionTitle: PropTypes.string,
    onAction: PropTypes.func,
    maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    allowBackdropClose: PropTypes.bool,
    allowEscapeClose: PropTypes.bool,
    showActions: PropTypes.bool,
    showCloseButton: PropTypes.bool,
};

export default MuiFullScreenDialog; 